import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import * as Yup from 'yup';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { AuthContext } from 'core/components/auth';
import ScreenHeader from 'core/components/screen-header';
import { Button } from 'core/components/button';
import Field from 'core/components/form/form.v2';
import { types } from 'core/helpers/sanitize';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import {
  FarmUWQuestionsCard,
  eligibilityQuestions,
  propertyRiskFactorQuestions,
  operationalDetailsQuestions,
  getFarmUWInitialValues
} from 'common/components/farm/uw-questions-form';
import useStyles from './pre-bind-farm-UW.styles';

const validationSchema = () => {
  const allQuestions = [...eligibilityQuestions, ...propertyRiskFactorQuestions, ...operationalDetailsQuestions];
  const schemaFields = {};

  allQuestions.forEach(({ id, showDetailsWhen }) => {
    const detailsRequiredWhenAnswer = showDetailsWhen ? showDetailsWhen[0] : 'true';

    schemaFields[id] = Yup.string().required();
    schemaFields[`${id}Details`] = Yup.string().when(id, {
      is: detailsRequiredWhenAnswer,
      then: (schema) => schema.required('Providing details is required'),
      otherwise: (schema) => schema
    });
  });

  schemaFields.signedFarmUWAttestation = Yup.boolean().oneOf([true], 'You must attest that your answers are accurate');

  return Yup.object().shape(schemaFields);
};

const PreBindFarmUW = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { offer: offerStore } = useStore();
  const { offerId } = useParams();
  const cleanedOfferId = extractCleanOfferId(offerId);

  const { offer, getOffer } = offerStore;
  const session = useContext(AuthContext);
  const {
    user: { username }
  } = session;

  const farmUWInitialValues = getFarmUWInitialValues(offerStore?.offer?.quote?.preBindUWCheck?.farmVerification);

  const submitRecalculate = async (details) => {
    const existingApprovalDetails = offerStore.offer?.quote?.preBindUWCheck?.approvalDetails;
    const formattedDetails = {
      ...offerStore.offer.quote,
      preBindUWCheck: {
        farmVerification: details,
        attestationAgent: username,
        ...(existingApprovalDetails && {
          approvalDetails: {
            sentToUnderwritingAt: null,
            sentToUnderwritingBy: null,
            underwriterApprovedAt: null,
            underwriterApprovedBy: null
          }
        })
      },
      selectedOption: offerStore.selectedOption
    };
    const cleanedDetails = types.QuoteDetailsInput(formattedDetails);
    await offerStore.recalculateQuoteToCluster(offerStore.offer.id, cleanedDetails, history);
  };

  if (!offer) {
    getOffer(cleanedOfferId);
    return (
      <Grid container className={classes.loadingContainer}>
        <Loading noBackground />
      </Grid>
    );
  }

  if (!offer.quote?.selectedOption?.includes('F')) {
    history.push(`/offer/${cleanedOfferId}`);
    return null;
  }

  if (!farmUWInitialValues || offerStore.loading) {
    return (
      <Grid container className={classes.loadingContainer}>
        <Loading noBackground />
      </Grid>
    );
  }

  return (
    <Formik
      validateOnChange
      initialValues={farmUWInitialValues}
      validationSchema={validationSchema}
      onSubmit={submitRecalculate}
      validateOnBlur={false}
    >
      {({ handleSubmit }) => (
        <Grid container className={classes.container}>
          <ScreenHeader
            title="Underwriting review"
            subtitles="This helps confirm that the farm meets eligibility requirements. It only takes a few seconds."
            onBack={() => {
              history.push(`/offer/${cleanedOfferId}`);
            }}
            className={classes.header}
          />

          <FarmUWQuestionsCard questions={eligibilityQuestions} label="Eligibility history" />
          <FarmUWQuestionsCard questions={propertyRiskFactorQuestions} label="Property risk factors" />
          <FarmUWQuestionsCard questions={operationalDetailsQuestions} label="Operational details" />

          {/* footer */}
          <Grid className={classes.floatingContainer}>
            <Grid className={classes.footerContainer}>
              <Field
                id="signedFarmUWAttestation"
                name="signedFarmUWAttestation"
                type="checkbox"
                label="I attest that the answers to these questions are true and accurate to the best of my knowledge."
                mode="light"
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} className={classes.saveButton}>
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
});

export default PreBindFarmUW;
